import { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Search, Eye, FileSpreadsheet, FileText, CheckCircle2, Banknote, Printer, HandCoins } from 'lucide-react';
import { toast } from 'sonner';
import { SupplierPaymentOrderDialog } from '../components/SupplierPaymentOrderDialog';
import { SupplierApplyAdvanceDialog } from '../components/SupplierApplyAdvanceDialog';
import { AdvanceApplicationsSection } from '../components/AdvanceApplicationsSection';
import { AdjustmentQuickButton } from '../components/AdjustmentQuickButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmtMoney = (n) => '$ ' + Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (iso) => { if (!iso) return '-'; const p = String(iso).slice(0, 10).split('-'); return p.length === 3 ? p.reverse().join('/') : iso; };
const userCan = (user, module, action) => {
  if (!user) return false;
  if (user.role === 'admin') return true;
  return user.permissions?.[module]?.[action] ?? false;
};

export default function SupplierAccounts() {
  const { user } = useOutletContext();
  const canPay = userCan(user, 'tesoreria_pagos', 'crear');
  const [data, setData] = useState({ rows: [], total_owed: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [opTarget, setOpTarget] = useState(null);
  const [opOpen, setOpOpen] = useState(false);
  const [applyAdvanceOpen, setApplyAdvanceOpen] = useState(false);  // v3.37.2
  const [showAllCc, setShowAllCc] = useState(false);  // v3.37.8 · false = sólo pendientes
  const detailMovements = (detail?.movements || []).filter(m => showAllCc || m.has_pending !== false);

  const openPayment = (supplierId, supplierName) => {
    setOpTarget({ supplierId, supplierName });
    setOpOpen(true);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/supplier-accounts/summary`, { withCredentials: true });
      setData(r.data || { rows: [], total_owed: 0 });
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al cargar cuentas corrientes'); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (supplierId) => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/supplier-accounts/${supplierId}`, { withCredentials: true });
      setDetail(r.data);
      setDetailOpen(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al cargar detalle'); }
  };

  const filtered = (data.rows || []).filter(r => {
    const s = search.toLowerCase().trim();
    if (!s) return true;
    return (r.business_name || '').toLowerCase().includes(s) || (r.tax_id || '').includes(s);
  }).sort((a, b) => {
    // Primero los con saldo adeudado (balance > 0), luego los "Al día".
    // Dentro de cada grupo, orden alfabético por razón social.
    const aOwes = (a.balance || 0) > 0 ? 1 : 0;
    const bOwes = (b.balance || 0) > 0 ? 1 : 0;
    if (aOwes !== bOwes) return bOwes - aOwes;
    return (a.business_name || '').localeCompare(b.business_name || '', 'es', { sensitivity: 'base' });
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-emerald-100 rounded-lg">
          <FileText className="w-6 h-6 text-emerald-700" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Cuenta Corriente de Proveedores</h1>
          <p className="text-sm text-muted-foreground">Deuda generada por Recepciones y Otras Compras</p>
        </div>
      </div>

      <Card><CardContent className="pt-6">
        <div className="flex items-center justify-between mb-4 gap-4">
          <div className="relative w-72">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
            <Input className="pl-8" placeholder="Buscar proveedor o CUIT…" value={search} onChange={e => setSearch(e.target.value)} data-testid="supplier-cc-search" />
          </div>
          <div className="flex items-center gap-4">
            <div className="flex gap-2">
              <Button variant="outline" size="sm"
                onClick={() => window.open(`${BACKEND_URL}/api/supplier-accounts/summary.pdf?t=${Date.now()}${search.trim() ? `&q=${encodeURIComponent(search.trim())}` : ''}`, '_blank')}
                data-testid="supplier-cc-summary-pdf-btn">
                <FileText className="w-4 h-4 mr-1" /> Exportar PDF
              </Button>
              <Button variant="outline" size="sm"
                onClick={() => window.open(`${BACKEND_URL}/api/supplier-accounts/summary.xlsx?t=${Date.now()}${search.trim() ? `&q=${encodeURIComponent(search.trim())}` : ''}`, '_blank')}
                data-testid="supplier-cc-summary-excel-btn">
                <FileSpreadsheet className="w-4 h-4 mr-1" /> Exportar Excel
              </Button>
            </div>
            <div className="text-right">
              <p className="text-xs text-muted-foreground">Total adeudado</p>
              <p className="text-xl font-bold text-emerald-700" data-testid="supplier-cc-total">{fmtMoney(data.total_owed)}</p>
            </div>
          </div>
        </div>

        {loading ? <p className="text-center py-8 text-muted-foreground">Cargando…</p> : (
          <Table data-testid="supplier-cc-table">
            <TableHeader><TableRow>
              <TableHead>Proveedor</TableHead><TableHead>CUIT</TableHead>
              <TableHead className="text-right">Compras</TableHead><TableHead className="text-right">Pagos/NC</TableHead>
              <TableHead className="text-right">Saldo adeudado</TableHead><TableHead className="w-10"></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.map((r) => (
                <TableRow key={r.supplier_id} data-testid={`supplier-cc-row-${r.supplier_id}`}>
                  <TableCell className="font-medium">{r.business_name}</TableCell>
                  <TableCell className="font-mono text-xs">{r.tax_id || '—'}</TableCell>
                  <TableCell className="text-right">{fmtMoney(r.total_credit)}</TableCell>
                  <TableCell className="text-right">{fmtMoney(r.total_debit)}</TableCell>
                  <TableCell className={`text-right font-semibold ${r.balance > 0.009 ? 'text-emerald-700' : (r.balance < -0.009 ? 'text-amber-700' : 'text-muted-foreground')}`}>
                    {r.balance > 0.009
                      ? fmtMoney(r.balance)
                      : r.balance < -0.009
                        ? <Badge variant="secondary" className="bg-amber-50 text-amber-800 border border-amber-200 font-normal" data-testid={`supplier-cc-advance-badge-${r.supplier_id}`}><HandCoins className="w-3 h-3 mr-1" />A favor {fmtMoney(Math.abs(r.balance))}</Badge>
                        : <Badge variant="secondary" className="bg-slate-100 text-slate-600 font-normal"><CheckCircle2 className="w-3 h-3 mr-1" />Al día</Badge>}
                  </TableCell>
                  <TableCell><div className="flex items-center justify-end gap-1">
                    {canPay && r.balance > 0 && (
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-emerald-700" onClick={() => openPayment(r.supplier_id, r.business_name)} data-testid={`supplier-cc-pay-${r.supplier_id}`} title="Nueva Orden de Pago"><Banknote className="w-4 h-4" /></Button>
                    )}
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openDetail(r.supplier_id)} data-testid={`supplier-cc-view-${r.supplier_id}`} title="Ver Mayor del proveedor"><Eye className="w-4 h-4" /></Button>
                  </div></TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Sin movimientos de cuenta corriente</TableCell></TableRow>}
            </TableBody>
          </Table>
        )}
      </CardContent></Card>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="supplier-cc-detail-dialog">
          <DialogHeader>
            <DialogTitle>{detail?.supplier?.business_name}</DialogTitle>
            <DialogDescription>Mayor de cuenta corriente · Saldo adeudado: <b>{fmtMoney(detail?.balance)}</b></DialogDescription>
          </DialogHeader>
          {/* v3.37.2 — Banner de saldo a favor disponible */}
          {detail?.available_advance > 0.009 && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 flex items-center justify-between" data-testid="supplier-advance-banner">
              <div className="flex items-center gap-2">
                <HandCoins className="w-5 h-5 text-emerald-600" />
                <div>
                  <div className="text-xs text-emerald-800 uppercase tracking-wide font-semibold">Saldo a favor disponible</div>
                  <div className="text-xl font-bold text-emerald-700">{fmtMoney(detail.available_advance)}</div>
                </div>
              </div>
              {canPay && (
                <Button
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => setApplyAdvanceOpen(true)}
                  data-testid="supplier-apply-advance-btn"
                >
                  <HandCoins className="w-4 h-4 mr-1" /> Aplicar a comprobantes
                </Button>
              )}
            </div>
          )}
          {detail?.supplier?.supplier_id && (
            <div className="flex justify-end gap-2 mb-2">
              {canPay && detail.balance > 0 && (
                <Button
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => { setDetailOpen(false); openPayment(detail.supplier.supplier_id, detail.supplier.business_name); }}
                  data-testid="supplier-detail-pay-btn"
                >
                  <Banknote className="w-4 h-4 mr-1" /> Nueva Orden de Pago
                </Button>
              )}
              <AdjustmentQuickButton
                targetType="supplier"
                targetId={detail.supplier.supplier_id}
                targetLabel={detail.supplier.business_name}
                onDone={() => openDetail(detail.supplier.supplier_id)}
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(`${BACKEND_URL}/api/supplier-accounts/${detail.supplier.supplier_id}/ledger.pdf?t=${Date.now()}`, '_blank')}
                data-testid="supplier-ledger-pdf-btn"
              >
                <FileText className="w-4 h-4 mr-1" /> Exportar PDF
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(`${BACKEND_URL}/api/supplier-accounts/${detail.supplier.supplier_id}/ledger.xlsx?t=${Date.now()}`, '_blank')}
                data-testid="supplier-ledger-excel-btn"
              >
                <FileSpreadsheet className="w-4 h-4 mr-1" /> Exportar Excel
              </Button>
            </div>
          )}
          {/* v3.37.8 — toggle sólo pendientes / historial completo */}
          <div className="flex items-center justify-between text-xs mb-2 border-t pt-2">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={showAllCc}
                onChange={e => setShowAllCc(e.target.checked)}
                data-testid="supplier-cc-show-all"
              />
              <span>Ver historial completo</span>
              {!showAllCc && <span className="ml-1 text-emerald-700">· sólo pendientes</span>}
            </label>
            <span className="text-muted-foreground">
              {detailMovements.length} / {(detail?.movements || []).length} movimiento(s)
            </span>
          </div>
          <Table>
            <TableHeader><TableRow>
              <TableHead className="w-28">Fecha</TableHead><TableHead>Detalle</TableHead>
              <TableHead className="text-right">Compra</TableHead><TableHead className="text-right">Pago/NC</TableHead>
              <TableHead className="text-right">Saldo</TableHead>
              <TableHead className="w-10"></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {detailMovements.map((m) => (
                <TableRow key={m.cc_id}>
                  <TableCell className="text-xs">{fmtDate(m.date)}</TableCell>
                  <TableCell className="text-sm">{m.description || m.ref_number}</TableCell>
                  <TableCell className="text-right">{m.credit > 0 ? fmtMoney(m.credit) : '—'}</TableCell>
                  <TableCell className="text-right">{m.debit > 0 ? fmtMoney(m.debit) : '—'}</TableCell>
                  <TableCell className="text-right font-medium">{fmtMoney(m.balance)}</TableCell>
                  <TableCell className="text-right">
                    {m.payment_order_id && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-emerald-700"
                        title={`Imprimir OP ${m.payment_order_number || ''}`}
                        onClick={() => window.open(`${BACKEND_URL}/api/treasury/payment-orders/${m.payment_order_id}/pdf?t=${Date.now()}`, '_blank')}
                        data-testid={`supplier-cc-op-pdf-${m.cc_id}`}
                      >
                        <Printer className="w-4 h-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {detailMovements.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">{(detail?.movements || []).length === 0 ? 'Sin movimientos' : 'Sin comprobantes pendientes · activá "Ver historial completo"'}</TableCell></TableRow>}
            </TableBody>
          </Table>
          {detail?.supplier?.supplier_id && (
            <div className="mt-4 pt-4 border-t">
              <AdvanceApplicationsSection
                entityType="supplier"
                entityId={detail.supplier.supplier_id}
                refreshKey={detail?.balance /* refresca cuando cambia el detalle */}
                onReversed={() => { openDetail(detail.supplier.supplier_id); load(); }}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      <SupplierPaymentOrderDialog
        open={opOpen}
        onOpenChange={setOpOpen}
        supplierId={opTarget?.supplierId}
        supplierName={opTarget?.supplierName}
        onSaved={() => { load(); if (detail?.supplier?.supplier_id) openDetail(detail.supplier.supplier_id); }}
      />
      <SupplierApplyAdvanceDialog
        open={applyAdvanceOpen}
        onOpenChange={setApplyAdvanceOpen}
        supplierId={detail?.supplier?.supplier_id}
        supplierName={detail?.supplier?.business_name}
        availableAdvance={detail?.available_advance || 0}
        onDone={() => { if (detail?.supplier?.supplier_id) openDetail(detail.supplier.supplier_id); load(); }}
      />
    </div>
  );
}
